import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DEFAULT_NOTE_CATEGORY,
  DEFAULT_NOTE_TITLE,
  NOTE_CATEGORIES,
  NOTE_CATEGORY_LABELS,
  NOTE_CATEGORY_OPTIONS,
  NOTE_TITLE_MAX_LENGTH,
  NOTE_TITLE_MIN_LENGTH,
  canPersistNoteDraft,
  sanitizeNoteContent,
  sanitizeNoteTitle,
  validateNoteContent,
} from "../constants/notes";
import {
  DEFAULT_NOTES_PREFERENCES,
  NOTES_CATEGORY_BADGE_TONES,
  NOTES_CATEGORY_FILTER_ALL,
  NOTES_LIST_DISPLAY_MODES,
  NOTES_SCROLL_ANCHORS,
  formatNoteDateShort,
  formatNoteSize,
  getNoteContentBytes,
  sanitizeNotesPreferences,
  sortNotesList,
} from "../constants/notesPreferences";
import {
  createNote,
  deleteNote,
  fetchNote,
  fetchNotesList,
  updateNote,
} from "../utils/notesApi";
import { LIBRARY_FONT_DEFAULT, LIBRARY_FONT_STEPS } from "../utils/libraryReading";
import { sanitizeNotesSpreadTypeface } from "./NotesSpreadFontDropdown";
import NotesSpread from "./NotesSpread";
import {
  getNotesScrollStorageKey,
  forgetNotesCaret,
  NOTES_SEARCH_SCOPES,
  NOTES_VIEW_MODES,
  readNotesCaret,
  readNotesScrollMap,
  readNotesSelection,
  readNotesViewMode,
  rememberNotesCaret,
  rememberNotesSelection,
  rememberNotesViewMode,
  writeNotesScrollMap,
} from "../utils/notesListPreference";
import { normalizeRegisterSearch } from "../utils/registerWordListDisplay";
import NotesConfirmModal from "./NotesConfirmModal";
import PackAssignDropdown from "./PackAssignDropdown";
import "./NotesWorkspace.css";
import "./WordEditPanel.css";
import "./WordListBox.css";

const UNSAVED_CHANGES_MESSAGE =
  "La note en cours a des modifications non enregistrées. Continuer sans enregistrer ?";

const DELETE_NOTE_CONFIRM_TITLE = "Supprimer la note";

const NOTE_CATEGORY_MENU_OPTIONS = NOTE_CATEGORY_OPTIONS.map((option) => ({
  ...option,
  dotClass: `notes-category-dot notes-category-dot--${
    NOTES_CATEGORY_BADGE_TONES[option.value] || NOTES_CATEGORY_BADGE_TONES[DEFAULT_NOTE_CATEGORY]
  }`,
}));

function filterNotesByCategory(list, categoryFilter) {
  if (!Array.isArray(list)) return [];
  if (categoryFilter === NOTES_CATEGORY_FILTER_ALL) return list;
  return list.filter((item) => item.category === categoryFilter);
}

function filterNotesBySearch(list, searchQuery, searchScope) {
  const normalizedQuery = normalizeRegisterSearch(searchQuery);
  if (!normalizedQuery) return Array.isArray(list) ? list : [];
  const scope =
    searchScope === NOTES_SEARCH_SCOPES.CONTENT
      ? NOTES_SEARCH_SCOPES.CONTENT
      : NOTES_SEARCH_SCOPES.TITLE;

  return (Array.isArray(list) ? list : []).filter((item) => {
    if (scope === NOTES_SEARCH_SCOPES.CONTENT) {
      return normalizeRegisterSearch(item?.content).includes(normalizedQuery);
    }
    return normalizeRegisterSearch(item?.title).includes(normalizedQuery);
  });
}

function resolveNoteIdForFilter(list, categoryFilter) {
  const filtered = filterNotesByCategory(list, categoryFilter);
  const ids = filtered.map((item) => item.noteId).filter(Boolean);
  const saved = readNotesSelection(categoryFilter);
  if (saved && ids.includes(saved)) return saved;
  return ids[0] || null;
}

function resolveNoteIdFromVisible(list, categoryFilter) {
  const ids = (Array.isArray(list) ? list : []).map((item) => item.noteId).filter(Boolean);
  const saved = readNotesSelection(categoryFilter);
  if (saved && ids.includes(saved)) return saved;
  return ids[0] || null;
}

const NotesWorkspaceContext = createContext(null);

function emptyDraft() {
  return {
    noteId: null,
    title: "",
    content: "",
    category: DEFAULT_NOTE_CATEGORY,
    contentBytes: 0,
    createdAt: null,
    updatedAt: null,
  };
}

function draftFromNote(note) {
  const content = sanitizeNoteContent(note?.content);
  const contentBytes =
    Number.isFinite(Number(note?.contentBytes)) && Number(note.contentBytes) >= 0
      ? Number(note.contentBytes)
      : getNoteContentBytes(content);

  return {
    noteId: note?.noteId || null,
    title: sanitizeNoteTitle(note?.title),
    content,
    category: note?.category || DEFAULT_NOTE_CATEGORY,
    contentBytes,
    createdAt: note?.createdAt || null,
    updatedAt: note?.updatedAt || null,
  };
}

function cloneDraft(draft) {
  return { ...draft };
}

function useNotesWorkspace() {
  const ctx = useContext(NotesWorkspaceContext);
  if (!ctx) {
    throw new Error("NotesWorkspace components must be used within NotesWorkspaceProvider.");
  }
  return ctx;
}

function resolveListItemContentBytes(item) {
  if (
    item?.contentBytes != null &&
    item.contentBytes !== "" &&
    Number.isFinite(Number(item.contentBytes)) &&
    Number(item.contentBytes) >= 0
  ) {
    return Number(item.contentBytes);
  }
  return getNoteContentBytes(item?.content);
}

function NotesListMetaBadge({ item, mode }) {
  if (mode === NOTES_LIST_DISPLAY_MODES.CATEGORY) {
    const category = item?.category || DEFAULT_NOTE_CATEGORY;
    const tone = NOTES_CATEGORY_BADGE_TONES[category] || NOTES_CATEGORY_BADGE_TONES[DEFAULT_NOTE_CATEGORY];
    const label = NOTE_CATEGORY_LABELS[category] || NOTE_CATEGORY_LABELS[DEFAULT_NOTE_CATEGORY];
    return (
      <span
        className={`word-list-meta-badge notes-list-meta-badge notes-list-meta-badge--category notes-list-meta-badge--category-${tone}`}
        title={label}
        aria-label={label}
      />
    );
  }

  let label = "—";
  if (item?.__local) {
    label = "—";
  } else if (mode === NOTES_LIST_DISPLAY_MODES.CREATED_AT) {
    label = formatNoteDateShort(item.createdAt);
  } else if (mode === NOTES_LIST_DISPLAY_MODES.UPDATED_AT) {
    label = formatNoteDateShort(item.updatedAt);
  } else if (mode === NOTES_LIST_DISPLAY_MODES.SIZE) {
    label = formatNoteSize(resolveListItemContentBytes(item));
  }

  return (
    <span className="word-list-meta-badge notes-list-meta-badge" title={label}>
      {label}
    </span>
  );
}

export const NotesWorkspaceProvider = forwardRef(function NotesWorkspaceProvider(
  {
    active = false,
    preferences = DEFAULT_NOTES_PREFERENCES,
    searchQuery = "",
    searchScope = NOTES_SEARCH_SCOPES.TITLE,
    onMetaChange = () => {},
    children,
  },
  ref
) {
  const notesPreferences = useMemo(
    () => sanitizeNotesPreferences(preferences),
    [preferences]
  );
  const notesSearchScope = useMemo(
    () =>
      searchScope === NOTES_SEARCH_SCOPES.CONTENT
        ? NOTES_SEARCH_SCOPES.CONTENT
        : NOTES_SEARCH_SCOPES.TITLE,
    [searchScope]
  );
  const notesSearchQuery = String(searchQuery || "");
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [baseline, setBaseline] = useState(emptyDraft);
  const [isEditing, setIsEditing] = useState(false);
  const [spreadOpen, setSpreadOpen] = useState(false);
  const [spreadFont, setSpreadFontState] = useState(LIBRARY_FONT_DEFAULT);
  const [spreadTypeface, setSpreadTypefaceState] = useState("georgia");
  const [spreadProgress, setSpreadProgress] = useState({ page: 1, total: 1 });
  const [spreadSeek, setSpreadSeek] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingNote, setLoadingNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState("");
  const draftRef = useRef(draft);
  const dirtyRef = useRef(dirty);
  const isEditingRef = useRef(isEditing);
  const categoryFilterRef = useRef(notesPreferences.categoryFilter);
  const selectingRef = useRef(false);
  const unsavedConfirmResolverRef = useRef(null);
  const deleteConfirmResolverRef = useRef(null);
  const previousNoteIdRef = useRef(null);
  const caretSnapshotRef = useRef({
    field: "content",
    start: null,
    end: null,
    scrollTop: 0,
  });

  const setCaretSnapshot = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== "object") return;
    caretSnapshotRef.current = {
      field: snapshot.field || "content",
      start: snapshot.start ?? null,
      end: snapshot.end ?? null,
      scrollTop: Number.isFinite(Number(snapshot.scrollTop))
        ? Number(snapshot.scrollTop)
        : 0,
    };
  }, []);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    categoryFilterRef.current = notesPreferences.categoryFilter;
  }, [notesPreferences.categoryFilter]);

  const canSave = dirty && isEditing && canPersistNoteDraft(draft);

  useEffect(() => {
    if (!active && spreadOpen) setSpreadOpen(false);
  }, [active, spreadOpen]);

  const openSpread = useCallback(() => {
    if (!draftRef.current.noteId && !isEditingRef.current) return;
    setSpreadSeek(1);
    setSpreadOpen(true);
  }, []);

  const closeSpread = useCallback(() => {
    setSpreadSeek(null);
    setSpreadOpen(false);
  }, []);

  const setSpreadFont = useCallback((size) => {
    setSpreadFontState(LIBRARY_FONT_STEPS.includes(size) ? size : LIBRARY_FONT_DEFAULT);
  }, []);

  const setSpreadTypeface = useCallback((value) => {
    setSpreadTypefaceState(sanitizeNotesSpreadTypeface(value));
  }, []);

  const seekSpread = useCallback((page) => {
    setSpreadSeek(Math.max(1, Math.round(Number(page) || 1)));
  }, []);

  const clearSpreadSeek = useCallback(() => setSpreadSeek(null), []);

  const reportSpread = useCallback((next) => {
    setSpreadProgress((current) =>
      current.page === next.page && current.total === next.total
        ? current
        : { page: next.page, total: next.total }
    );
  }, []);

  useEffect(() => {
    onMetaChange({
      dirty: canSave,
      isEditing,
      hasNote: Boolean(draft.noteId) || isEditing,
      spreadOpen: active && spreadOpen,
      spreadPage: spreadProgress.page,
      spreadTotal: spreadProgress.total,
      spreadFont,
      spreadTypeface,
    });
  }, [
    active,
    canSave,
    isEditing,
    draft.noteId,
    onMetaChange,
    spreadOpen,
    spreadProgress,
    spreadFont,
    spreadTypeface,
  ]);

  const closeUnsavedConfirm = useCallback((confirmed) => {
    setUnsavedConfirmOpen(false);
    const resolve = unsavedConfirmResolverRef.current;
    unsavedConfirmResolverRef.current = null;
    resolve?.(Boolean(confirmed));
  }, []);

  const requestDiscardUnsaved = useCallback(() => {
    if (!dirtyRef.current) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      unsavedConfirmResolverRef.current = resolve;
      setUnsavedConfirmOpen(true);
    });
  }, []);

  const closeDeleteConfirm = useCallback((confirmed) => {
    setDeleteConfirmOpen(false);
    const resolve = deleteConfirmResolverRef.current;
    deleteConfirmResolverRef.current = null;
    resolve?.(Boolean(confirmed));
  }, []);

  const requestDeleteConfirm = useCallback((title) => {
    const label = String(title || "").trim() || "cette note";
    setDeleteConfirmMessage(
      `Supprimer définitivement « ${label} » ? Cette action est irréversible.`
    );
    return new Promise((resolve) => {
      deleteConfirmResolverRef.current = resolve;
      setDeleteConfirmOpen(true);
    });
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const list = (await fetchNotesList()).map((item) => ({
        ...item,
        noteId: item?.noteId || null,
        title: sanitizeNoteTitle(item?.title),
        content: sanitizeNoteContent(item?.content),
      }));
      setItems(list);
      return list;
    } catch (err) {
      setError(err.message || "Impossible de charger les notes.");
      setItems([]);
      return [];
    } finally {
      setLoadingList(false);
    }
  }, []);

  const applyLoadedNote = useCallback((note, { keepEditing = false } = {}) => {
    const next = draftFromNote(note);
    setDraft(next);
    setBaseline(cloneDraft(next));
    setDirty(false);
    if (keepEditing) {
      setIsEditing(true);
    } else {
      setIsEditing(readNotesViewMode() === NOTES_VIEW_MODES.EDIT);
    }
    setError(null);
  }, []);

  const selectNote = useCallback(
    async (noteId, { persist = true } = {}) => {
      if (!noteId) {
        setDraft(emptyDraft());
        setBaseline(emptyDraft());
        setDirty(false);
        setIsEditing(false);
        if (persist) {
          rememberNotesSelection(categoryFilterRef.current, null);
        }
        return true;
      }

      if (draftRef.current.noteId === noteId) {
        if (persist) {
          rememberNotesSelection(categoryFilterRef.current, noteId);
        }
        return true;
      }

      if (dirtyRef.current) {
        const ok = await requestDiscardUnsaved();
        if (!ok) return false;
      }

      selectingRef.current = true;
      setLoadingNote(true);
      setError(null);
      try {
        const note = await fetchNote(noteId);
        applyLoadedNote(note);
        if (persist) {
          rememberNotesSelection(categoryFilterRef.current, noteId);
        }
        return true;
      } catch (err) {
        setError(err.message || "Impossible de charger la note.");
        return false;
      } finally {
        setLoadingNote(false);
        selectingRef.current = false;
      }
    },
    [applyLoadedNote, requestDiscardUnsaved]
  );

  const filteredItems = useMemo(() => {
    const byCategory = filterNotesByCategory(items, notesPreferences.categoryFilter);
    const bySearch = filterNotesBySearch(byCategory, notesSearchQuery, notesSearchScope);
    return sortNotesList(bySearch, notesPreferences.sortOrder);
  }, [
    items,
    notesPreferences.categoryFilter,
    notesPreferences.sortOrder,
    notesSearchQuery,
    notesSearchScope,
  ]);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    (async () => {
      await loadList();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [active, loadList]);

  // Restaure la note mémorisée pour le filtre / recherche courants.
  useEffect(() => {
    if (!active || loadingList || selectingRef.current) return;
    // Brouillon local (nouvelle note pas encore enregistrée) : ne pas écraser.
    if (!draftRef.current.noteId && isEditingRef.current) return;
    const targetId = resolveNoteIdFromVisible(
      filteredItems,
      notesPreferences.categoryFilter
    );
    const currentId = draftRef.current.noteId || null;
    if ((targetId || null) === currentId) {
      if (targetId) {
        rememberNotesSelection(notesPreferences.categoryFilter, targetId);
        // Au retour registre → carnet, réappliquer le mode œil/main mémorisé.
        const shouldEdit = readNotesViewMode() === NOTES_VIEW_MODES.EDIT;
        if (shouldEdit !== isEditingRef.current) {
          setIsEditing(shouldEdit);
        }
      } else if (currentId) {
        selectNote(null);
      }
      return;
    }
    selectNote(targetId);
  }, [
    active,
    loadingList,
    filteredItems,
    notesPreferences.categoryFilter,
    selectNote,
  ]);

  const patchDraft = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  }, []);

  const startEditing = useCallback(() => {
    if (!draftRef.current.noteId || isEditingRef.current) return;
    rememberNotesViewMode(NOTES_VIEW_MODES.EDIT);
    setIsEditing(true);
    setError(null);
  }, []);

  const cancelEditing = useCallback(async () => {
    const current = draftRef.current;
    if (!current.noteId) {
      const fallback =
        previousNoteIdRef.current ||
        resolveNoteIdForFilter(items, categoryFilterRef.current);
      previousNoteIdRef.current = null;
      setDirty(false);
      rememberNotesViewMode(NOTES_VIEW_MODES.READ);
      setIsEditing(false);
      setError(null);
      if (fallback) {
        await selectNote(fallback);
      } else {
        setDraft(emptyDraft());
        setBaseline(emptyDraft());
      }
      return;
    }

    setDraft(cloneDraft(baseline));
    setDirty(false);
    rememberNotesViewMode(NOTES_VIEW_MODES.READ);
    setIsEditing(false);
    setError(null);
  }, [baseline, items, selectNote]);

  const enterReadMode = useCallback(async () => {
    if (!isEditingRef.current) return;
    if (dirtyRef.current) {
      const ok = await requestDiscardUnsaved();
      if (!ok) return;
    }
    rememberNotesViewMode(NOTES_VIEW_MODES.READ);
    await cancelEditing();
  }, [cancelEditing, requestDiscardUnsaved]);

  const handleCreate = useCallback(async () => {
    // Création réservée au mode lecture.
    if (isEditingRef.current) return;

    const preferredCategory =
      notesPreferences.categoryFilter !== NOTES_CATEGORY_FILTER_ALL
        ? notesPreferences.categoryFilter
        : DEFAULT_NOTE_CATEGORY;

    previousNoteIdRef.current = draftRef.current.noteId;
    selectingRef.current = true;
    const next = {
      noteId: null,
      title: DEFAULT_NOTE_TITLE,
      content: "",
      category: preferredCategory,
      contentBytes: 0,
      createdAt: null,
      updatedAt: null,
    };
    setDraft(next);
    setBaseline(cloneDraft(next));
    setDirty(false);
    rememberNotesViewMode(NOTES_VIEW_MODES.EDIT);
    setIsEditing(true);
    setError(null);
    selectingRef.current = false;
  }, [notesPreferences.categoryFilter]);

  const handleSave = useCallback(async () => {
    const current = draftRef.current;
    if (!dirtyRef.current && current.noteId) return;

    const title = sanitizeNoteTitle(current.title);
    const content = sanitizeNoteContent(current.content);
    if (title.length < NOTE_TITLE_MIN_LENGTH || title.length > NOTE_TITLE_MAX_LENGTH) {
      setError(
        `Le titre doit contenir entre ${NOTE_TITLE_MIN_LENGTH} et ${NOTE_TITLE_MAX_LENGTH} caractères.`
      );
      return;
    }
    const contentError = validateNoteContent(content);
    if (contentError) {
      setError(contentError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        title,
        content,
        category: current.category || NOTE_CATEGORIES.AUTRE,
      };
      const saved = current.noteId
        ? await updateNote(current.noteId, payload)
        : await createNote(payload);
      rememberNotesSelection(categoryFilterRef.current, saved.noteId);
      previousNoteIdRef.current = null;
      rememberNotesCaret(saved.noteId, {
        ...caretSnapshotRef.current,
        // Si le curseur n'a pas encore été capturé, garder la fin du contenu.
        start:
          caretSnapshotRef.current.start != null
            ? caretSnapshotRef.current.start
            : String(saved.content || "").length,
        end:
          caretSnapshotRef.current.end != null
            ? caretSnapshotRef.current.end
            : String(saved.content || "").length,
      });
      // Rester en édition après enregistrement (comme un document Word / Bloc-notes).
      rememberNotesViewMode(NOTES_VIEW_MODES.EDIT);
      applyLoadedNote(saved, { keepEditing: true });
      setIsEditing(true);
      await loadList();
    } catch (err) {
      setError(err.message || "Impossible d'enregistrer la note.");
    } finally {
      setSaving(false);
    }
  }, [applyLoadedNote, loadList]);

  const handleDelete = useCallback(async () => {
    const current = draftRef.current;
    if (!current.noteId) {
      if (dirtyRef.current) {
        const ok = await requestDiscardUnsaved();
        if (!ok) return;
      }
      await cancelEditing();
      return;
    }

    const ok = await requestDeleteConfirm(current.title);
    if (!ok) return;

    setSaving(true);
    setError(null);
    try {
      await deleteNote(current.noteId);
      forgetNotesCaret(current.noteId);
      setDraft(emptyDraft());
      setBaseline(emptyDraft());
      setDirty(false);
      setIsEditing(false);
      const filter = notesPreferences.categoryFilter;
      rememberNotesSelection(filter, null);
      const list = await loadList();
      const nextId = resolveNoteIdForFilter(list, filter);
      await selectNote(nextId);
    } catch (err) {
      setError(err.message || "Impossible de supprimer la note.");
    } finally {
      setSaving(false);
    }
  }, [
    cancelEditing,
    loadList,
    notesPreferences.categoryFilter,
    requestDeleteConfirm,
    requestDiscardUnsaved,
    selectNote,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      create: handleCreate,
      save: handleSave,
      delete: handleDelete,
      read: enterReadMode,
      edit: startEditing,
      openSpread,
      closeSpread,
      setSpreadFont,
      setSpreadTypeface,
      seekSpread,
    }),
    [handleCreate, handleSave, handleDelete, enterReadMode, startEditing, openSpread, closeSpread, setSpreadFont, setSpreadTypeface, seekSpread]
  );

  const listItems = useMemo(() => {
    if (!(isEditing && !draft.noteId)) return filteredItems;
    const localItem = {
      noteId: null,
      title: draft.title || DEFAULT_NOTE_TITLE,
      category: draft.category || DEFAULT_NOTE_CATEGORY,
      content: draft.content || "",
      contentBytes: getNoteContentBytes(draft.content),
      createdAt: null,
      updatedAt: null,
      __local: true,
    };
    return [localItem, ...filteredItems];
  }, [filteredItems, isEditing, draft.noteId, draft.title, draft.category, draft.content]);

  const value = useMemo(
    () => ({
      active,
      items: listItems,
      draft,
      dirty,
      canSave,
      isEditing,
      loadingList,
      loadingNote,
      saving,
      error,
      preferences: notesPreferences,
      selectNote,
      patchDraft,
      startEditing,
      enterReadMode,
      cancelEditing,
      handleSave,
      handleDelete,
      setCaretSnapshot,
      spreadOpen,
      spreadFont,
      spreadTypeface,
      spreadSeek,
      openSpread,
      closeSpread,
      reportSpread,
      clearSpreadSeek,
    }),
    [
      active,
      listItems,
      draft,
      dirty,
      canSave,
      isEditing,
      loadingList,
      loadingNote,
      saving,
      error,
      notesPreferences,
      selectNote,
      patchDraft,
      startEditing,
      enterReadMode,
      cancelEditing,
      handleSave,
      handleDelete,
      setCaretSnapshot,
      spreadOpen,
      spreadFont,
      spreadTypeface,
      spreadSeek,
      openSpread,
      closeSpread,
      reportSpread,
      clearSpreadSeek,
    ]
  );

  return (
    <NotesWorkspaceContext.Provider value={value}>
      {children}
      <NotesConfirmModal
        open={unsavedConfirmOpen}
        title="Modifications non enregistrées"
        message={UNSAVED_CHANGES_MESSAGE}
        confirmLabel="Continuer"
        cancelLabel="Annuler"
        onConfirm={() => closeUnsavedConfirm(true)}
        onCancel={() => closeUnsavedConfirm(false)}
      />
      <NotesConfirmModal
        open={deleteConfirmOpen}
        title={DELETE_NOTE_CONFIRM_TITLE}
        message={deleteConfirmMessage}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        onConfirm={() => closeDeleteConfirm(true)}
        onCancel={() => closeDeleteConfirm(false)}
      />
    </NotesWorkspaceContext.Provider>
  );
});

export function NotesListPanel() {
  const { active, items, draft, loadingList, selectNote, preferences } = useNotesWorkspace();
  const listRef = useRef(null);
  const itemRefs = useRef({});
  const scrollPositionsRef = useRef(null);
  if (scrollPositionsRef.current === null) {
    scrollPositionsRef.current = readNotesScrollMap();
  }
  const scrollStorageKey = getNotesScrollStorageKey(preferences.categoryFilter);
  const scrollStorageKeyRef = useRef(scrollStorageKey);
  const pendingRestoreRef = useRef(true);
  const persistTimerRef = useRef(null);
  const itemsRef = useRef(items);
  const selectedNoteIdRef = useRef(draft.noteId);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  itemsRef.current = items;
  selectedNoteIdRef.current = draft.noteId;

  if (scrollStorageKeyRef.current !== scrollStorageKey) {
    scrollStorageKeyRef.current = scrollStorageKey;
    pendingRestoreRef.current = true;
  }

  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;
    setScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, []);

  const flushScroll = useCallback(() => {
    const el = listRef.current;
    if (el && el.clientHeight > 0) {
      scrollPositionsRef.current[scrollStorageKeyRef.current] = el.scrollTop;
    }
    writeNotesScrollMap(scrollPositionsRef.current);
  }, []);

  const tryRestoreScroll = useCallback(() => {
    if (!pendingRestoreRef.current) return;
    const el = listRef.current;
    if (!el || el.clientHeight === 0) return;

    const list = itemsRef.current;
    const idx = list.findIndex((item) => item.noteId === selectedNoteIdRef.current);
    if (idx >= 0 && !itemRefs.current[idx]) return;

    const saved = scrollPositionsRef.current[scrollStorageKeyRef.current];
    if (typeof saved === "number" && saved > 0) {
      el.scrollTop = saved;
    }

    const item = idx >= 0 ? itemRefs.current[idx] : null;
    if (item) {
      const elRect = el.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const fullyVisible =
        itemRect.top >= elRect.top && itemRect.bottom <= elRect.bottom;
      if (!fullyVisible) {
        const delta =
          itemRect.top - elRect.top - el.clientHeight / 2 + itemRect.height / 2;
        el.scrollTop += delta;
      }
    }

    pendingRestoreRef.current = false;
    updateScrollFades();
  }, [updateScrollFades]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (el) {
      scrollPositionsRef.current[scrollStorageKey] = el.scrollTop;
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        writeNotesScrollMap(scrollPositionsRef.current);
      }, 120);
    }
    updateScrollFades();
  }, [scrollStorageKey, updateScrollFades]);

  useEffect(() => {
    if (loadingList) return;
    pendingRestoreRef.current = true;
    let frame1;
    let frame2;
    frame1 = requestAnimationFrame(() => {
      tryRestoreScroll();
      frame2 = requestAnimationFrame(() => tryRestoreScroll());
    });
    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [loadingList, items, scrollStorageKey, tryRestoreScroll]);

  useEffect(() => {
    const onBeforeUnload = () => flushScroll();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      flushScroll();
    };
  }, [flushScroll]);

  const prevActiveRef = useRef(active);
  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = active;
    if (active && !wasActive) {
      pendingRestoreRef.current = true;
      requestAnimationFrame(() => tryRestoreScroll());
    } else if (!active && wasActive) {
      flushScroll();
    }
  }, [active, tryRestoreScroll, flushScroll]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      updateScrollFades();
      tryRestoreScroll();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollFades, tryRestoreScroll]);

  return (
    <div
      className={[
        "word-listbox-shell",
        scrollFades.top && "word-listbox-shell--fade-top",
        scrollFades.bottom && "word-listbox-shell--fade-bottom",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Liste des notes"
    >
      <div ref={listRef} className="listbox custom-scroll" onScroll={handleScroll}>
        {loadingList && items.length === 0 ? (
          <div className="notes-list-empty">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="notes-list-empty">Aucune note pour le moment</div>
        ) : (
          items.map((item, index) => {
            const selected = item.__local
              ? !draft.noteId
              : item.noteId === draft.noteId;
            return (
              <div
                key={item.__local ? "__local_draft__" : item.noteId}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={`word-list-item${selected ? " word-list-item--selected" : ""}`}
                onClick={() => {
                  if (item.__local) return;
                  selectNote(item.noteId);
                }}
              >
                <span className="word-list-item__label">{item.title}</span>
                <NotesListMetaBadge item={item} mode={preferences.listDisplayMode} />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export function NotesDetailPanel() {
  const {
    draft,
    isEditing,
    loadingNote,
    saving,
    error,
    preferences,
    canSave,
    patchDraft,
    handleSave,
    setCaretSnapshot,
    spreadOpen,
    spreadFont,
    spreadTypeface,
    spreadSeek,
    reportSpread,
    clearSpreadSeek,
  } = useNotesWorkspace();
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const contentFieldRef = useRef(null);
  const titleFieldRef = useRef(null);
  const categoryFieldRef = useRef(null);
  const editFocusRef = useRef({ field: "content", start: null, end: null, scrollTop: 0 });
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });
  const anchorSessionRef = useRef(false);
  const anchorRafRef = useRef(0);
  const anchorStartRef = useRef(0);
  const anchorStableRef = useRef(0);
  const lastAnchoredTopRef = useRef(null);
  const wasEditingRef = useRef(false);
  const wasSavingRef = useRef(false);

  useEffect(() => {
    if (!isEditing) return undefined;

    const onKeyDown = (event) => {
      const isSaveShortcut =
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        String(event.key || "").toLowerCase() === "s";
      if (!isSaveShortcut) return;
      event.preventDefault();
      event.stopPropagation();
      if (saving || !canSave) return;
      handleSave();
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isEditing, saving, canSave, handleSave]);

  const publishCaretSnapshot = useCallback(
    (field, target) => {
      const start =
        target && typeof target.selectionStart === "number" ? target.selectionStart : null;
      const end =
        target && typeof target.selectionEnd === "number" ? target.selectionEnd : null;
      const scrollTop =
        field === "content" && contentFieldRef.current
          ? contentFieldRef.current.scrollTop
          : 0;
      const snapshot = { field, start, end, scrollTop };
      editFocusRef.current = snapshot;
      setCaretSnapshot?.(snapshot);
    },
    [setCaretSnapshot]
  );

  const rememberEditFocus = useCallback(
    (field, target) => {
      if (!target) return;
      publishCaretSnapshot(field, target);
    },
    [publishCaretSnapshot]
  );

  const restoreEditFocus = useCallback((snapshot = editFocusRef.current) => {
    const snap = snapshot || editFocusRef.current;
    const fieldMap = {
      title: titleFieldRef,
      category: categoryFieldRef,
      content: contentFieldRef,
    };
    const el = fieldMap[snap?.field]?.current || contentFieldRef.current;
    if (!el) return false;
    el.focus({ preventScroll: true });
    if (
      typeof el.setSelectionRange === "function" &&
      typeof snap?.start === "number" &&
      typeof snap?.end === "number"
    ) {
      const max = typeof el.value === "string" ? el.value.length : 0;
      const start = Math.max(0, Math.min(snap.start, max));
      const end = Math.max(0, Math.min(snap.end, max));
      try {
        el.setSelectionRange(start, end);
      } catch {
        // select/option n'accepte pas setSelectionRange
      }
    }
    if (snap?.field === "content" && contentFieldRef.current) {
      const textarea = contentFieldRef.current;
      if (typeof snap.scrollTop === "number") {
        textarea.scrollTop = snap.scrollTop;
      } else if (typeof snap.start === "number" && textarea.value.length > 0) {
        const ratio = snap.start / textarea.value.length;
        textarea.scrollTop = Math.max(
          0,
          textarea.scrollHeight * ratio - textarea.clientHeight / 2
        );
      }
    }
    return true;
  }, []);

  const updateScrollFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;
    setScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, []);

  useEffect(() => {
    updateScrollFades();
  }, [draft, isEditing, loadingNote, updateScrollFades]);

  // Au début d'un enregistrement : figer la position actuelle du curseur.
  useEffect(() => {
    if (!saving || !isEditing) return;
    const field = editFocusRef.current.field || "content";
    const target =
      field === "title"
        ? titleFieldRef.current
        : field === "category"
          ? categoryFieldRef.current
          : contentFieldRef.current;
    publishCaretSnapshot(field, target || contentFieldRef.current);
  }, [saving, isEditing, publishCaretSnapshot]);

  // Après un enregistrement : remettre le curseur dans le champ édité.
  useEffect(() => {
    const wasSaving = wasSavingRef.current;
    wasSavingRef.current = saving;
    if (!isEditing || saving || !wasSaving) return undefined;
    const frame = requestAnimationFrame(() => restoreEditFocus());
    return () => cancelAnimationFrame(frame);
  }, [saving, isEditing, restoreEditFocus]);

  // Entrée en édition : restaurer le curseur du dernier enregistrement, sinon fin du texte.
  useLayoutEffect(() => {
    const justEnteredEditing = isEditing && !wasEditingRef.current;
    wasEditingRef.current = isEditing;
    if (!justEnteredEditing) return undefined;

    anchorSessionRef.current = false;
    cancelAnimationFrame(anchorRafRef.current);

    let cancelled = false;
    let attempts = 0;
    let rafId = 0;
    let timeoutId = 0;
    const savedCaret = draft.noteId ? readNotesCaret(draft.noteId) : null;

    const applyCaret = () => {
      if (cancelled) return false;

      if (savedCaret && (savedCaret.start != null || savedCaret.field !== "content")) {
        const textarea = contentFieldRef.current;
        if (savedCaret.field === "content" && !textarea) return false;
        editFocusRef.current = savedCaret;
        setCaretSnapshot?.(savedCaret);
        const ok = restoreEditFocus(savedCaret);
        updateScrollFades();
        return ok;
      }

      const textarea = contentFieldRef.current;
      if (!textarea) return false;
      const value = typeof textarea.value === "string" ? textarea.value : "";
      const end = value.length;
      const fallback = {
        field: "content",
        start: end,
        end,
        scrollTop: textarea.scrollHeight,
      };
      editFocusRef.current = fallback;
      setCaretSnapshot?.(fallback);
      textarea.focus({ preventScroll: true });
      try {
        textarea.setSelectionRange(end, end);
      } catch {
        // ignore
      }
      textarea.scrollTop = textarea.scrollHeight;
      updateScrollFades();
      return true;
    };

    const tick = () => {
      if (cancelled) return;
      const ok = applyCaret();
      attempts += 1;
      if (ok && attempts >= 2) return;
      if (attempts >= 12) return;
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    timeoutId = window.setTimeout(() => {
      if (!cancelled) applyCaret();
    }, 80);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [isEditing, draft.noteId, restoreEditFocus, setCaretSnapshot, updateScrollFades]);

  const applyScrollAnchorPass = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return false;

    const anchor = preferences.scrollAnchor;
    if (!anchor || anchor === NOTES_SCROLL_ANCHORS.FREE) return true;

    if (anchor !== NOTES_SCROLL_ANCHORS.PAGE_END) return true;

    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const desired = maxTop;

    if (Math.abs(desired - container.scrollTop) > 1) {
      container.scrollTop = desired;
      anchorStableRef.current = 0;
    } else {
      anchorStableRef.current += 1;
    }
    lastAnchoredTopRef.current = container.scrollTop;
    return anchorStableRef.current >= 3;
  }, [preferences.scrollAnchor]);

  // Ancrage de scroll en mode lecture uniquement (pas en édition).
  useEffect(() => {
    if (isEditing || spreadOpen) return undefined;

    const anchor = preferences.scrollAnchor;
    if (!anchor || anchor === NOTES_SCROLL_ANCHORS.FREE) return undefined;

    anchorSessionRef.current = true;
    anchorStartRef.current =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    anchorStableRef.current = 0;
    lastAnchoredTopRef.current = null;
    cancelAnimationFrame(anchorRafRef.current);

    const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
    const MAX_DURATION_MS = 2500;

    const tick = () => {
      if (!anchorSessionRef.current) return;
      const container = scrollRef.current;

      if (
        lastAnchoredTopRef.current != null &&
        container &&
        Math.abs(container.scrollTop - lastAnchoredTopRef.current) > 2
      ) {
        anchorSessionRef.current = false;
        return;
      }

      const done = applyScrollAnchorPass();
      if (done || now() - anchorStartRef.current > MAX_DURATION_MS) {
        anchorSessionRef.current = false;
        return;
      }
      anchorRafRef.current = requestAnimationFrame(tick);
    };

    anchorRafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(anchorRafRef.current);
      anchorSessionRef.current = false;
    };
  }, [draft.noteId, isEditing, spreadOpen, preferences.scrollAnchor, applyScrollAnchorPass]);

  const categoryLabel =
    NOTE_CATEGORY_LABELS[draft.category] || NOTE_CATEGORY_LABELS[DEFAULT_NOTE_CATEGORY];
  const categoryTone =
    NOTES_CATEGORY_BADGE_TONES[draft.category] ||
    NOTES_CATEGORY_BADGE_TONES[DEFAULT_NOTE_CATEGORY];

  const titleChars = draft.title ? Array.from(draft.title) : [];
  const titleInitial = (titleChars[0] ?? "").toUpperCase();
  const titleRest = titleChars.slice(1).join("");
  const initialIsAccented = /[\u0300-\u036f]/.test(titleInitial.normalize("NFD"));

  if (spreadOpen) {
    return (
      <NotesSpread
        title={draft.title}
        content={draft.content}
        editing={isEditing}
        fontSize={spreadFont}
        typeface={spreadTypeface}
        seekPage={spreadSeek}
        onSeekApplied={clearSpreadSeek}
        onTitleChange={(value) =>
          patchDraft({ title: String(value || "").slice(0, NOTE_TITLE_MAX_LENGTH) })
        }
        onContentChange={(value) => patchDraft({ content: value })}
        onProgress={reportSpread}
      />
    );
  }

  return (
    <div
      className={[
        "containerDetailMot-shell",
        isEditing && "notes-detail-shell--editing",
        scrollFades.top && "containerDetailMot-shell--fade-top",
        scrollFades.bottom && "containerDetailMot-shell--fade-bottom",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Contenu de la note"
    >
      <div
        ref={scrollRef}
        className={[
          "containerDetailMot",
          "detail-scroll",
          isEditing && "notes-detail-scroll--editing",
        ]
          .filter(Boolean)
          .join(" ")}
        onScroll={updateScrollFades}
      >
        <div
          ref={contentRef}
          className={[
            "containerDetailMot__content",
            isEditing && "notes-detail-content--editing",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {!draft.noteId && !isEditing ? (
            <p className="notes-detail-empty">
              Choisissez un autre filtre de rubrique ou créez une nouvelle note
              en cliquant sur l'icône +.
            </p>
          ) : (
            <div
              className={[
                "word-edit-panel",
                "notes-detail-panel",
                isEditing && "notes-detail-panel--editing",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {!isEditing ? (
                <h2
                  className="word-detail-title"
                  style={{
                    textAlign: "center",
                    margin: "0px 0 20px 0",
                    fontSize: "calc(26px * var(--text-scale, 1))",
                    color: "#39210d",
                  }}
                >
                  <span
                    className={`word-detail-title__initial${
                      initialIsAccented ? " word-detail-title__initial--accented" : ""
                    }`}
                  >
                    {titleInitial}
                  </span>
                  {titleRest}
                </h2>
              ) : null}

              <div
                className={
                  isEditing ? "notes-detail-edit-body" : "notes-detail-read-body"
                }
              >
                {isEditing ? (
                  <>
                    <div className="notes-detail-edit-meta">
                      <div className="notes-detail-field">
                        <input
                          id="notes-edit-title"
                          ref={titleFieldRef}
                          type="text"
                          className={`notes-detail-field__input notes-detail-field__input--tone-${
                            NOTES_CATEGORY_BADGE_TONES[draft.category] ||
                            NOTES_CATEGORY_BADGE_TONES[DEFAULT_NOTE_CATEGORY]
                          }`}
                          value={draft.title}
                          maxLength={NOTE_TITLE_MAX_LENGTH}
                          disabled={loadingNote}
                          placeholder="Titre de la note"
                          aria-label="Titre de la note"
                          onChange={(event) => patchDraft({ title: event.target.value })}
                          onSelect={(event) => rememberEditFocus("title", event.target)}
                          onKeyUp={(event) => rememberEditFocus("title", event.target)}
                          onBlur={(event) => rememberEditFocus("title", event.target)}
                        />
                      </div>

                      <div
                        className="notes-detail-field notes-detail-field--select"
                        ref={categoryFieldRef}
                        tabIndex={-1}
                        onFocus={() => rememberEditFocus("category", categoryFieldRef.current)}
                        onBlur={() => rememberEditFocus("category", categoryFieldRef.current)}
                      >
                        <PackAssignDropdown
                          options={NOTE_CATEGORY_MENU_OPTIONS}
                          value={draft.category}
                          onChange={(next) => patchDraft({ category: next })}
                          disabled={loadingNote}
                          ariaLabel="Catégorie de la note"
                          matchTriggerWidth
                          menuMaxHeight={240}
                          menuZIndex={2147483600}
                          menuClassName="notes-detail-category-menu"
                        />
                      </div>
                    </div>

                    <div className="notes-detail-edit-content" data-scroll-anchor="content">
                      <textarea
                        id="notes-edit-content"
                        ref={contentFieldRef}
                        className="notes-detail-textarea"
                        value={draft.content}
                        disabled={loadingNote}
                        placeholder="Écrire la note…"
                        aria-label="Contenu de la note"
                        onChange={(event) => {
                          rememberEditFocus("content", event.target);
                          patchDraft({ content: event.target.value });
                        }}
                        onSelect={(event) => rememberEditFocus("content", event.target)}
                        onKeyUp={(event) => rememberEditFocus("content", event.target)}
                        onClick={(event) => rememberEditFocus("content", event.target)}
                        onBlur={(event) => rememberEditFocus("content", event.target)}
                        onScroll={(event) => {
                          editFocusRef.current = {
                            ...editFocusRef.current,
                            field: "content",
                            scrollTop: event.currentTarget.scrollTop,
                          };
                          setCaretSnapshot?.(editFocusRef.current);
                        }}
                        spellCheck
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="notes-detail-category">
                      <span
                        className={`notes-detail-category-pill notes-detail-category-pill--${categoryTone}`}
                      >
                        <span className="notes-detail-category-pill__dot" aria-hidden="true" />
                        <span className="notes-detail-category-pill__label">{categoryLabel}</span>
                      </span>
                    </div>

                    <div style={{ marginBottom: "20px" }} data-scroll-anchor="content">
                      <p className="notes-detail-content-text">
                        {draft.content?.trim() ? draft.content : "—"}
                      </p>
                    </div>
                  </>
                )}

                {error ? <p className="notes-detail-error">{error}</p> : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
